import useFaqT from "./useFaqT";
import FaqAnswerBlock from "./FaqAnswerBlock";
import { buildMatchParts } from "../../utils/faqSearch";
import { FAQ_SHOW_KEYS } from "../../constants/faq/faqConfig";

// Accordion row (design F4 + sheet A): min 56px, hover/open #FAFBFC, chevron
// rotation, two-line wrap never truncate. Every row owns a stable anchor id =
// its question key (`/faq#<question-key>`, InfoDot-ready); a deep-linked row
// flashes briefly. Mobile (<md): the mono anchor key is hidden (design F7 rows
// show only question + chevron).
//
// The role pill is GONE (2026-08-10). It existed to mark foreign-role rows that
// "Show all questions" revealed — and that toggle no longer exists, because a
// viewer now only ever receives the questions of their own tiers (Marin: "der
// Agent und Agency darf sicher nicht wissen was alles die Clubs haben!"). Every
// row this component renders is by definition the viewer's own, so there is
// nothing left to mark.
//
// The deep-link flash is a plain ring, NOT `animate-faq-pulse`: both render as
// box-shadow, so the animation overwrote the static ring and the highlight was
// effectively invisible (measured across a full cycle: peak alpha .27 at
// 1.3px). The pulse token stays where the design puts it — the marker click
// frame in the media overlay. The ring is drawn OUTSIDE the box (no
// `ring-inset`) with `z-10`: an inset ring disappears behind the opened
// question button, which paints an opaque #FAFBFC over exactly the edge the
// reader is looking at.
const FaqAccordionRow = ({
  question,
  query,
  open,
  onToggle,
  imgSet,
  onSetImgSet,
  flash,
}) => {
  const tr = useFaqT();
  const parts = buildMatchParts(tr(question.q), query);
  const buttonId = `faq-row-${question.id}-button`;
  const contentId = `faq-row-${question.id}-content`;

  return (
    <div
      id={question.id}
      className={`scroll-mt-24 border-b border-[#EDEEF0] ${
        flash ? "relative z-10 ring-2 ring-[#1F5EFF]" : ""
      }`}
    >
      <button
        type="button"
        id={buttonId}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={onToggle}
        className={`flex min-h-[56px] w-full items-start gap-3 px-1 py-4 text-left hover:bg-[#FAFBFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F5EFF] md:gap-4 md:py-[18px] ${
          open ? "bg-[#FAFBFC]" : "bg-transparent"
        }`}
      >
        <span className="flex-1 text-[15px] font-medium leading-[1.4] text-[#14171C] md:text-[16px]">
          {parts.map((p, i) =>
            p.hit ? (
              <mark key={i} className="bg-[#FFF0A8] text-inherit shadow-[0_0_0_1px_#F0DE7A]">
                {p.t}
              </mark>
            ) : (
              <span key={i}>{p.t}</span>
            )
          )}
        </span>
        {FAQ_SHOW_KEYS && (
          <span className="mt-1 hidden flex-none font-faqmono text-[10px] text-[#B4B9C2] md:block">
            #{question.id}
          </span>
        )}
        <span
          aria-hidden="true"
          className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center text-[12px] text-[#8A919D] transition-transform ${
            open ? "rotate-180" : "rotate-0"
          }`}
        >
          ▾
        </span>
      </button>

      {open && (
        <FaqAnswerBlock
          question={question}
          imgSet={imgSet}
          onSetImgSet={onSetImgSet}
          labelledBy={buttonId}
          contentId={contentId}
        />
      )}
    </div>
  );
};

export default FaqAccordionRow;
